import React from 'react';

interface ComponentTypeIconProps {
  type: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const ComponentTypeIcon: React.FC<ComponentTypeIconProps> = ({ 
  type, 
  size = 'md', 
  className = '' 
}) => {
  // Clean the type name by removing numeric prefixes
  const cleanType = type.replace(/^\d+-/, '');
  
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  const renderIcon = () => {
    switch (cleanType.toLowerCase()) {
      case 'atoms':
        return (
          <div className={`${sizeClasses[size]} ${className}`}>
            {/* Simple circle representing the most basic building block */}
            <div className="w-full h-full bg-blue-500 rounded-full flex items-center justify-center">
              <div className="w-1/2 h-1/2 bg-white rounded-full"></div>
            </div>
          </div>
        );
      
      case 'molecules':
        return (
          <div className={`${sizeClasses[size]} ${className}`}>
            {/* Two connected circles representing simple combinations */}
            <div className="w-full h-full flex items-center justify-center space-x-1">
              <div className="w-1/2 h-1/2 bg-green-500 rounded-full"></div>
              <div className="w-1/2 h-1/2 bg-green-500 rounded-full"></div>
            </div>
          </div>
        );
      
      case 'organisms':
        return (
          <div className={`${sizeClasses[size]} ${className}`}>
            {/* Three layered elements representing complex structures */}
            <div className="w-full h-full flex flex-col items-center justify-center space-y-1">
              <div className="w-3/4 h-1/3 bg-purple-500 rounded"></div>
              <div className="w-full h-1/3 bg-purple-500 rounded"></div>
              <div className="w-3/4 h-1/3 bg-purple-500 rounded"></div>
            </div>
          </div>
        );
      
      case 'templates':
        return (
          <div className={`${sizeClasses[size]} ${className}`}>
            {/* Grid structure representing layout templates */}
            <div className="w-full h-full bg-orange-500 rounded p-1">
              <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-0.5">
                <div className="bg-white rounded-sm"></div>
                <div className="bg-white rounded-sm"></div>
                <div className="bg-white rounded-sm"></div>
                <div className="bg-white rounded-sm"></div>
              </div>
            </div>
          </div>
        );
      
      case 'pages':
        return (
          <div className={`${sizeClasses[size]} ${className}`}>
            {/* Document/page shape representing complete views */}
            <div className="w-full h-full bg-red-500 rounded flex items-center justify-center relative">
              <div className="w-3/4 h-3/4 bg-white rounded-sm flex flex-col items-center justify-center">
                <div className="w-1/2 h-1 h-1 bg-red-500 rounded-full mb-1"></div>
                <div className="w-2/3 h-1 h-1 bg-red-500 rounded-full mb-1"></div>
                <div className="w-1/2 h-1 h-1 bg-red-500 rounded-full"></div>
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className={`${sizeClasses[size]} ${className}`}>
            {/* Generic icon for unknown types */}
            <div className="w-full h-full bg-gray-500 rounded flex items-center justify-center">
              <div className="w-1/2 h-1/2 bg-white rounded"></div>
            </div>
          </div>
        );
    }
  };

  return renderIcon();
};

export default ComponentTypeIcon;
